import { useState, useEffect } from 'react'
import { apiClient, Product, Checkpoint, ProductState, Statistics, getApiError } from './api'
import { UploadScreen } from './components/UploadScreen'
import { ResumeSessionScreen } from './components/ResumeSessionScreen'
import { ConferenceScreen } from './components/ConferenceScreen'
import { CompletionScreen } from './components/CompletionScreen'
import { DashboardScreen } from './components/DashboardScreen'
import { GalleryScreen } from './components/GalleryScreen'
import { ExternalUploadScreen } from './components/ExternalUploadScreen'
import { ManualSearchScreen } from './components/ManualSearchScreen'
import { BrandDashboardScreen } from './components/BrandDashboardScreen'
import { UserMenu } from './components/UserMenu'
import { AlertCircle } from 'lucide-react'
import './index.css'
import { supabase } from './supabase'

type AppState = 'loading' | 'upload' | 'resume' | 'conference' | 'completion' | 'dashboard' | 'gallery' | 'external-upload' | 'brands' | 'manual-search' | 'error'

function App() {
  const [appState, setAppState] = useState<AppState>('loading')
  const [checkpoint, setCheckpoint] = useState<Checkpoint | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [products, setProducts] = useState<Product[]>([])
  const [manualSearchBack, setManualSearchBack] = useState<AppState>('upload')
  const [brandsBack, setBrandsBack] = useState<AppState>('dashboard')
  const [currentProduct, setCurrentProduct] = useState<{ product: Product; state: ProductState; stats: Statistics; checkpoint: { index: number; total: number } } | null>(() => {
    // Recuperar do localStorage ao iniciar
    const cached = localStorage.getItem('currentProduct')
    return cached ? JSON.parse(cached) : null
  })

  useEffect(() => {
    initializeApp()
  }, [])

  // Salvar currentProduct no localStorage sempre que mudar
  useEffect(() => {
    if (currentProduct) {
      localStorage.setItem('currentProduct', JSON.stringify(currentProduct))
    }
  }, [currentProduct])

  const initializeApp = async () => {
    try {
      // Verificar se servidor está acessível
      await apiClient.health()

      // Tentar recuperar checkpoint existente
      const response = await apiClient.getCheckpoint()
      const existingCheckpoint = response.data.checkpoint

      if (existingCheckpoint && existingCheckpoint.status === 'in_progress') {
        setCheckpoint(existingCheckpoint)
        setAppState('resume')
      } else {
        setAppState('upload')
      }
    } catch (err) {
      console.error('Erro ao inicializar:', err)
      setErrorMessage('Não conseguiu conectar ao servidor. Verifique se ele está rodando em http://localhost:5000')
      setAppState('error')
    }
  }

  const handleProductsLoaded = async (productsData: Product[]) => {
    try {
      setProducts(productsData)
      setAppState('loading')
      const response = await apiClient.init(productsData)

      setCheckpoint(response.data.checkpoint)

      // Se for sessão nova, começar conferência direto
      if (response.data.message === 'Nova sessão iniciada') {
        setAppState('conference')
      } else {
        setAppState('resume')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao inicializar'
      setErrorMessage(message)
      setAppState('error')
    }
  }

  const handleContinueSession = async () => {
    try {
      setAppState('loading')
      const response = await apiClient.resumeSession()
      setCheckpoint(response.data.checkpoint)
      setAppState('conference')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao retomar sessão'
      setErrorMessage(message)
      setAppState('error')
    }
  }

  const handleNewSession = async () => {
    try {
      setAppState('upload')
      setCheckpoint(null)
      setCurrentProduct(null)
      localStorage.removeItem('currentProduct')
    } catch (err) {
      console.error(err)
    }
  }

  const handleSessionCompleted = () => {
    // Mostrar tela de conclusão
    setAppState('completion')
    // Limpar cache do produto atual
    localStorage.removeItem('currentProduct')
  }

  const handleGoToDashboard = () => {
    setAppState('dashboard')
  }

  const handleBackToCon = () => {
    setAppState('conference')
  }

  const handleGoToDashboardFromConference = () => {
    setAppState('dashboard')
    // Não zera currentProduct - mantém o cache
  }

  const handleGoToExternalUpload = () => {
    setAppState('external-upload')
  }

  const handleGoToGallery = () => {
    setAppState('gallery')
    // Não zera currentProduct - mantém o cache
  }

  const handleGoToBrandsFromConference = () => {
    setBrandsBack('conference')
    setAppState('brands')
  }

  const handleManageBrandProduct = async (codigo: string) => {
    try {
      setAppState('loading')
      const response = await apiClient.jumpToProduct(codigo)
      setCurrentProduct(response.data)
      setBrandsBack('conference')
      setAppState('conference')
    } catch (err) {
      setErrorMessage(getApiError(err, 'Erro ao abrir produto no gerenciamento de imagens'))
      setAppState('error')
    }
  }

  const handleBackFromGallery = () => {
    setAppState('conference')
  }

  const handleError = (error: string) => {
    setErrorMessage(error)
  }

  const handleNewConference = async () => {
    try {
      await apiClient.resetSession()
      setAppState('upload')
      setCheckpoint(null)
    } catch (err) {
      setErrorMessage('Erro ao iniciar nova conferência')
    }
  }

  const handleClearAllData = async () => {
    try {
      await apiClient.clearAllData()
      setAppState('upload')
      setCheckpoint(null)
    } catch (err) {
      setErrorMessage('Erro ao limpar dados')
    }
  }

  const handleViewHistory = () => {
    alert('Histórico será implementado em breve')
  }

  const handleClearHistory = async () => {
    try {
      await apiClient.clearHistory()
      alert('Histórico deletado')
    } catch (err) {
      setErrorMessage('Erro ao limpar histórico')
    }
  }

  if (appState === 'error') {
    return (
      <div className="min-h-screen bg-gradient-light dark:bg-gradient-dark flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-8 max-w-md w-full text-center border border-gray-200 dark:border-slate-700">
          <AlertCircle size={64} className="mx-auto mb-4 text-[#E5191F] dark:text-[#FCD016]" />
          <h1 className="text-2xl font-bold text-[#414142] dark:text-white mb-4">Erro</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">{errorMessage}</p>
          <button
            onClick={() => {
              setErrorMessage(null)
              initializeApp()
            }}
            className="bg-gradient-to-r from-[#E5191F] to-[#F03B40] dark:from-[#D9B20F] dark:to-[#FCD016] hover:from-[#C91419] hover:to-[#E01A36] dark:hover:from-[#C9A00A] dark:hover:to-[#F0BA00] text-white dark:text-[#18181A] font-semibold py-3 px-6 rounded-lg transition"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    )
  }

  if (appState === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-light dark:bg-gradient-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin mb-4">
            <svg className="h-12 w-12 text-[#E5191F] dark:text-[#FCD016] mx-auto" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <p className="text-gray-600 dark:text-gray-300">Carregando...</p>
        </div>
      </div>
    )
  }

  if (appState === 'upload') {
    return <>
      <UploadScreen onProductsLoaded={handleProductsLoaded} isLoading={false} />
      <UserMenu />
    </>
  }

  if (appState === 'resume' && checkpoint) {
    return (<>
      <ResumeSessionScreen
        checkpoint={checkpoint}
        onContinue={handleContinueSession}
        onNewSession={handleNewSession}
        isLoading={false}
        onManualSearch={() => { setManualSearchBack('resume'); setAppState('manual-search') }}
      />
      <UserMenu />
    </>)
  }

  if (appState === 'conference') {
    return (
      <>
      <ConferenceScreen
        onSessionCompleted={handleSessionCompleted}
        onError={handleError}
        onGoToDashboard={handleGoToDashboardFromConference}
        onGoToGallery={handleGoToGallery}
        onGoToBrands={handleGoToBrandsFromConference}
        onGoToExternalUpload={handleGoToExternalUpload}
        onGoToManualSearch={() => { setManualSearchBack('conference'); setAppState('manual-search') }}
        products={products}
        currentProductData={currentProduct}
        onCurrentProductLoaded={(data) => setCurrentProduct(data)}
      />
      <UserMenu />
      </>
    )
  }

  if (appState === 'gallery') {
    return (<>
      <GalleryScreen onBack={handleBackFromGallery} />
      <UserMenu />
    </>)
  }

  if (appState === 'external-upload') {
    return <><ExternalUploadScreen onBack={() => setAppState('conference')} /><UserMenu /></>
  }

  if (appState === 'manual-search') {
    return <><ManualSearchScreen onBack={() => setAppState(manualSearchBack)} /><UserMenu /></>
  }

  if (appState === 'completion') {
    return (
      <CompletionScreen
        stats={{
          approved: checkpoint?.approved || 0,
          noImage: checkpoint?.noImage || 0,
          skipped: checkpoint?.skipped || 0,
          pending: 0,
          searched: 0
        }}
        totalProducts={checkpoint?.totalProducts || 0}
        onNewSession={handleNewConference}
        onViewHistory={handleViewHistory}
        onClearAll={handleClearAllData}
        onGoToDashboard={() => setAppState('dashboard')}
      />
    )
  }

  if (appState === 'brands') {
    return <><BrandDashboardScreen onBack={() => setAppState(brandsBack)} onManageProduct={handleManageBrandProduct} /><UserMenu /></>
  }

  if (appState === 'dashboard') {
    return (
      <><DashboardScreen
        onBack={handleBackToCon}
        onClearAll={handleClearAllData}
        onBrands={() => { setBrandsBack('dashboard'); setAppState('brands') }}
      /><UserMenu /></>
    )
  }

  return null
}

export default App
